type ImageWithMeta = { id: string; meta: { order: number } };

export function swapImageOrders(
    images: ImageWithMeta[],
    fromOrder: number,
    toOrder: number,
): ImageWithMeta[] {
    let updatedImages = images.map((img) => ({ ...img, meta: { ...img.meta } }));
    const draggedImage = updatedImages.find((img) => img.meta.order === fromOrder);
    const targetImage = updatedImages.find((img) => img.meta.order === toOrder);
    if (!draggedImage) return images;
    if (targetImage) {
        [draggedImage.meta.order, targetImage.meta.order] = [
            targetImage.meta.order,
            draggedImage.meta.order,
        ];
    } else {
        draggedImage.meta.order = toOrder;
    }
    return updatedImages.sort((a, b) => a.meta.order - b.meta.order);
}
export function isVisualSettingsChanged(a: any, b: any) {
    if (!a || !b) return false;
    return Object.keys(a).some((key) => a[key] !== b[key]);
}

export function isVisualEffectsChanged(a: any, b: any) {
    if (!a || !b) return false;
    return a.name !== b.name || a.intensity !== b.intensity;
}

export function isCanvasColorChanged(a: any, b: any) {
    if (!a || !b) return false;
    return (
        a.red !== b.red ||
        a.green !== b.green ||
        a.blue !== b.blue ||
        a.gradient !== b.gradient ||
        a.transparency !== b.transparency
    );
}
export function isTransformationChanged(a: any, b: any) {
    if (!a || !b) return false;

    if ((!a.crop && b.crop) || (a.crop && !b.crop)) {
        return true;
    }

    return (
        a.width !== b.width ||
        a.height !== b.height ||
        a.rotation !== b.rotation ||
        a.flip !== b.flip ||
        a.presets?.size !== b.presets?.size ||
        (a.crop &&
            b.crop &&
            (a.crop.x !== b.crop.x ||
                a.crop.y !== b.crop.y ||
                a.crop.width !== b.crop.width ||
                a.crop.height !== b.crop.height ||
                a.crop.zoom !== b.crop.zoom))
    );
}
export function isLayoutChanged(a: any, b: any) {
    if (!a) return false;
    if (b === null) {
        return a.slug !== 'none';
    }
    if (!b) return false;

    const result =
        a.slug !== b.slug ||
        a.gap_size !== b.gap_size ||
        isCanvasColorChanged(a.canvas_color, b.canvas_color) ||
        a.order !== b.order;

    return result;
}

export function normalizeCanvasColorGradient(canvasColor: any) {
    if (canvasColor && canvasColor.gradient === 'o') {
        return { ...canvasColor, gradient: 'none' };
    }
    return canvasColor;
}

export function getImageSize(url: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = function () {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = reject;
        img.src = url;
    });
}

export function rotateImageFile(file: File, angle: number = 90): Promise<File> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function (e) {
            const img = new window.Image();
            img.onload = function () {
                const canvas = document.createElement('canvas');
                if (angle % 180 === 0) {
                    canvas.width = img.width;
                    canvas.height = img.height;
                } else {
                    canvas.width = img.height;
                    canvas.height = img.width;
                }
                const ctx = canvas.getContext('2d');
                if (!ctx) return reject('No canvas context');
                ctx.save();

                if (angle === 90) {
                    ctx.translate(canvas.width, 0);
                    ctx.rotate((90 * Math.PI) / 180);
                } else if (angle === 180) {
                    ctx.translate(canvas.width, canvas.height);
                    ctx.rotate((180 * Math.PI) / 180);
                } else if (angle === 270) {
                    ctx.translate(0, canvas.height);
                    ctx.rotate((270 * Math.PI) / 180);
                }
                ctx.drawImage(img, 0, 0);
                ctx.restore();
                canvas.toBlob((blob) => {
                    if (!blob) return reject('Failed to create blob');
                    resolve(new File([blob], file.name, { type: file.type }));
                }, file.type);
            };
            img.onerror = reject;
            img.src = e.target?.result as string;
        };
        reader.onerror = reject;

        reader.readAsDataURL(file);
    });
}
